<template>
  <div class="page-body">
    <div class="page-head">
      <div>
        <h1>WhatsApp Campaigns</h1>
        <p>Sync WhatsApp templates, create campaigns, and track delivery.</p>
      </div>

      <button class="primary-btn" @click="syncTemplates" :disabled="syncing">
        {{ syncing ? "Syncing..." : "Sync Templates" }}
      </button>
    </div>

    <div class="wa-grid">
      <!-- LEFT -->
      <div class="panel template-panel">
        <div class="panel-head">
          <h3>Template Master</h3>
        </div>

        <div class="toolbar">
          <div class="search-box">
            <input v-model="templateSearch" placeholder="Search template..." />
          </div>

          <select v-model="templateStatus" class="filter-select">
            <option value="">All</option>
            <option value="APPROVED">Approved</option>
            <option value="PENDING">Pending</option>
            <option value="REJECTED">Rejected</option>
          </select>

          <button class="manage-btn" @click="loadTemplates">Search</button>
        </div>

        <div v-if="templates.length === 0" class="empty-state">
          No templates found
        </div>

        <div class="template-list">
          <div
            v-for="tpl in templates"
            :key="tpl.id"
            class="template-card"
            :class="{ active: selectedTemplate?.id === tpl.id }"
            @click="selectTemplate(tpl)"
          >
            <div class="template-top">
              <strong>{{ tpl.template_name }}</strong>
              <span :class="['status-badge', statusClass(tpl.status)]">
                {{ tpl.status }}
              </span>
            </div>

            <div class="template-meta">
              {{ tpl.category || "-" }} · {{ tpl.language || "-" }} ·
              {{ getTemplateHeaderType(tpl) }}
            </div>

            <p>{{ tpl.body_text }}</p>
          </div>
        </div>
      </div>

      <!-- RIGHT -->
      <div class="panel">
        <div class="panel-head">
          <h3>Create Campaign</h3>
        </div>

        <div class="safe-settings">
          <h4>Safe Sending Settings</h4>

          <div class="safe-grid">
            <div>
              <label>Batch Size</label>
              <input type="number" v-model.number="batchSize" min="1" max="50" />
              <small>How many messages picked in one cycle. Start with 5–10.</small>
            </div>

            <div>
              <label>Min Delay MS</label>
              <input type="number" v-model.number="minDelayMs" min="500" />
              <small>Minimum wait before each message. 1500 = 1.5 sec.</small>
            </div>

            <div>
              <label>Max Delay MS</label>
              <input type="number" v-model.number="maxDelayMs" min="1000" />
              <small>Maximum random wait. Keep higher than min delay.</small>
            </div>

            <div>
              <label>Daily Limit</label>
              <input type="number" v-model.number="dailyLimit" min="1" />
              <small>Max messages per day. Use low value for new numbers.</small>
            </div>
          </div>
        </div>

        <div class="campaign-builder-grid">
          <div class="campaign-inputs">
            <div class="form-group">
              <label>Campaign Name</label>
              <input v-model="campaignName" placeholder="Festival Offer Campaign" />
            </div>

            <div class="form-group">
              <label>Selected Template</label>
              <input
                :value="selectedTemplate?.template_name || ''"
                readonly
                placeholder="Select template from left"
              />
            </div>

            <div v-if="selectedTemplate" class="param-info">
              Body Params Required:
              <b>{{ bodyParamCount }}</b>
            </div>

            <div v-if="selectedTemplate && isMediaHeader" class="media-box">
              <label>
                Upload {{ getTemplateHeaderType(selectedTemplate) }} Header Media
                <span class="required">*</span>
              </label>

              <input type="file" :accept="mediaAccept" @change="uploadHeaderMedia" />

              <div v-if="globalHeaderMediaUrl" class="uploaded-media fallback-media">
                <strong>Uploaded:</strong>
                <a :href="globalHeaderMediaUrl" target="_blank">
                  {{ globalHeaderMediaUrl }}
                </a>
              </div>

              <div
                v-else-if="selectedTemplate?.default_header_media_url"
                class="uploaded-media fallback-media"
              >
                <strong>Using saved template media:</strong>
                <a :href="selectedTemplate.default_header_media_url" target="_blank">
                  Preview
                </a>
              </div>

              <div
                v-else-if="getTemplateHeaderExampleMedia(selectedTemplate)"
                class="uploaded-media warning-media"
              >
                <strong>Preview media only:</strong>
                <a :href="getTemplateHeaderExampleMedia(selectedTemplate)" target="_blank">
                  Meta Preview
                </a>
                <small>This cannot be used for sending. Sync should save it first.</small>
              </div>

              <input
                v-if="getTemplateHeaderType(selectedTemplate) === 'DOCUMENT'"
                v-model="globalDocumentFilename"
                placeholder="Document filename e.g. invoice.pdf"
              />

              <small>
                Upload media before creating campaign if no saved template media is available.
              </small>
            </div>

            <div class="import-box">
              <label>Import Recipients CSV / Excel</label>

              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                @change="importRecipientsFile"
              />

              <small>Columns: phone, name, param1, param2, param3...</small>
            </div>
          </div>

          <div class="campaign-preview">
            <div v-if="selectedTemplate" class="preview-box preview-sticky">
              <WaTemplatePreview
                :templateJson="selectedTemplate.components_json"
                :headerMedia="getUsableHeaderMediaUrl()"
                :documentFilename="
                  globalDocumentFilename ||
                  selectedTemplate.default_document_filename ||
                  'document.pdf'
                "
              />
            </div>

            <div v-else class="preview-empty">
              Select a template to preview WhatsApp message
            </div>
          </div>
        </div>

        <div class="steps-header">
          <h3>Recipients</h3>
          <button class="small-btn" @click="addRecipient">+ Add Row</button>
        </div>

        <div
          v-for="(r, index) in recipients"
          :key="index"
          class="recipient-row"
        >
          <input v-model="r.phone" placeholder="Phone e.g. 919999999999" />
          <input v-model="r.name" placeholder="Name" />
          <input
            v-model="r.bodyParamsText"
            placeholder="Body params comma separated"
          />

          <button class="remove-btn" @click="removeRecipient(index)">×</button>
        </div>

        <div class="safe-settings">
          <h4>Schedule Campaign</h4>

          <div class="schedule-grid">
            <label class="toggle-row">
              <span>Schedule for later</span>

              <label class="switch">
                <input type="checkbox" v-model="isScheduled" />
                <span class="slider"></span>
              </label>
            </label>

            <div class="datetime-wrapper" v-if="isScheduled" @click="openPicker">
              <span class="date-icon">📅</span>

              <input
                ref="dateRef"
                type="datetime-local"
                v-model="scheduledAt"
                class="datetime-input"
                :min="minScheduleDateTime"
              />
            </div>
          </div>

          <small v-if="isScheduled">
            Campaign will start automatically.
            <span class="hint" v-if="scheduledAt">
              {{ formatPreviewDate(scheduledAt) }}
            </span>
          </small>
        </div>

        <button
          class="primary-btn full-btn"
          @click="createCampaign"
          :disabled="creating"
        >
          {{ creating ? "Creating..." : "Create Campaign" }}
        </button>
      </div>
    </div>

    <div class="panel campaign-panel">
      <div class="panel-head">
        <h3>Campaigns</h3>
        <button class="manage-btn" @click="loadCampaigns">Refresh</button>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Campaign</th>
              <th>Template</th>
              <th>Status</th>
              <th>Scheduled At</th>
              <th>Total</th>
              <th>Sent</th>
              <th>Delivered</th>
              <th>Read</th>
              <th>Failed</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="c in campaigns" :key="c.id">
              <td>{{ c.id }}</td>
              <td>{{ c.campaign_name }}</td>
              <td>{{ c.template_name }}</td>
              <td>
                <span :class="['status-badge', statusClass(c.status)]">
                  {{ c.status }}
                </span>
              </td>
              <td>{{ c.scheduled_at || "-" }}</td>
              <td>{{ c.total_count }}</td>
              <td>{{ c.sent_count }}</td>
              <td>{{ c.delivered_count || 0 }}</td>
              <td>{{ c.read_count || 0 }}</td>
              <td>{{ c.failed_count }}</td>
              <td>
                <div class="action-group">
                  <button
                    v-if="c.status === 'DRAFT' || c.status === 'PAUSED'"
                    class="test-btn"
                    @click="startCampaign(c.id)"
                  >
                    Start
                  </button>

                  <button
                    v-if="c.status === 'RUNNING'"
                    class="warning-btn"
                    @click="pauseCampaign(c.id)"
                  >
                    Pause
                  </button>

                  <button class="edit-btn" @click="viewRecipients(c.id)">
                    Recipients
                  </button>

                  <button class="export-btn" @click="exportRecipients(c.id, c.campaign_name)">
                    Export
                  </button>
                </div>
              </td>
              

            </tr>

            <tr v-if="campaigns.length === 0">
              <td colspan="11" class="empty-td">No campaigns found</td>
            </tr>
          </tbody>
        </table>

        <div class="pagination">
          <div class="page-info">
            Page {{ campaignPage }} of {{ campaignTotalPages }}
            · Total {{ campaignTotal }}
          </div>

          <div class="page-actions">
            <select v-model="campaignLimit" @change="changeCampaignLimit">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>

            <button class="manage-btn" @click="prevCampaignPage" :disabled="campaignPage <= 1">
              Prev
            </button>

            <button class="manage-btn" @click="nextCampaignPage" :disabled="campaignPage >= campaignTotalPages">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showRecipients" class="modal-overlay" @click.self="showRecipients = false">
      <div class="modal">
        <div class="modal-head">
          <h2>Campaign Recipients</h2>
          <button class="close-btn" @click="showRecipients = false">×</button>
        </div>

        <div class="modal-body">
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Phone</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Delivery</th>
                  <th>Delivered At</th>
                  <th>Read At</th>
                  <th>Error</th>
                  <th>Sent At</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="r in campaignRecipients" :key="r.id">
                  <td>{{ r.phone }}</td>
                  <td>{{ r.name }}</td>
                  <td>
                    <span :class="['status-badge', statusClass(r.status)]">
                      {{ r.status }}
                    </span>
                  </td>
                  <td>{{ r.delivery_status || "-" }}</td>
                  <td>{{ r.delivered_at || "-" }}</td>
                  <td>{{ r.read_at || "-" }}</td>
                  <td>{{ formatWaError(r.error_message) }}</td>
                  <td>{{ r.sent_at || "-" }}</td>
                </tr>

                <tr v-if="campaignRecipients.length === 0">
                  <td colspan="8" class="empty-td">No recipients found</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div v-if="toast.show" :class="['toast', toast.type]">
      {{ toast.message }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import VARS from "@/services/vars";
import { auth } from "@/services/auth";
import * as XLSX from "xlsx";
import WaTemplatePreview from "@/components/WaTemplatePreview.vue";

const API_BASE = VARS.API_URL;

const templates = ref([]);
const campaigns = ref([]);
const campaignRecipients = ref([]);

const batchSize = ref(5);
const minDelayMs = ref(1500);
const maxDelayMs = ref(6000);
const dailyLimit = ref(50);

const campaignPage = ref(1);
const campaignLimit = ref(10);
const campaignTotal = ref(0);

const globalHeaderMediaUrl = ref("");
const globalDocumentFilename = ref("");

const isScheduled = ref(false);
const scheduledAt = ref("");
const dateRef = ref(null);

const selectedTemplate = ref(null);
const templateSearch = ref("");
const templateStatus = ref("");

const campaignName = ref("");
const recipients = ref([
  {
    phone: "",
    name: "",
    bodyParamsText: "",
    headerMediaUrl: "",
    documentFilename: "",
  },
]);

const syncing = ref(false);
const creating = ref(false);
const showRecipients = ref(false);

let refreshTimer = null;

const toast = ref({
  show: false,
  type: "success",
  message: "",
});

const bodyParamCount = computed(() => {
  if (!selectedTemplate.value?.body_text) return 0;
  const matches = selectedTemplate.value.body_text.match(/{{\d+}}/g);
  return matches ? matches.length : 0;
});

const campaignTotalPages = computed(() => {
  return Math.max(1, Math.ceil(campaignTotal.value / campaignLimit.value));
});

const minScheduleDateTime = computed(() => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 2);

  const pad = n => String(n).padStart(2, "0");

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate()
  )}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
});

const isMediaHeader = computed(() => {
  const type = getTemplateHeaderType(selectedTemplate.value);
  return ["IMAGE", "VIDEO", "DOCUMENT"].includes(type);
});

const mediaAccept = computed(() => {
  const type = getTemplateHeaderType(selectedTemplate.value);

  if (type === "IMAGE") return "image/png,image/jpeg,image/jpg";
  if (type === "VIDEO") return "video/mp4";
  if (type === "DOCUMENT") return ".pdf,.doc,.docx,.xls,.xlsx";

  return "*";
});

function showToast(message, type = "success") {
  toast.value = { show: true, type, message };

  setTimeout(() => {
    toast.value.show = false;
  }, 3000);
}

function statusClass(status) {
  const s = String(status || "").toUpperCase();

  if (["APPROVED", "RUNNING", "SENT", "COMPLETED"].includes(s)) return "active";
  if (["PENDING", "DRAFT", "PAUSED", "SCHEDULED"].includes(s)) return "draft";
  if (["REJECTED", "FAILED"].includes(s)) return "failed";

  return "draft";
}

function selectTemplate(tpl) {
  selectedTemplate.value = tpl;
  globalHeaderMediaUrl.value = "";
  globalDocumentFilename.value = "";
}

function openPicker() {
  if (dateRef.value?.showPicker) {
    dateRef.value.showPicker();
  } else {
    dateRef.value?.focus();
  }
}

function formatPreviewDate(val) {
  if (!val) return "";

  const d = new Date(val);

  return d.toLocaleString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getUsableHeaderMediaUrl() {
  return (
    globalHeaderMediaUrl.value ||
    selectedTemplate.value?.default_header_media_url ||
    ""
  );
}

function getTemplateHeaderType(tpl) {
  if (!tpl) return "NONE";

  if (tpl.header_type) {
    return String(tpl.header_type).toUpperCase();
  }

  try {
    const components =
      typeof tpl.components_json === "string"
        ? JSON.parse(tpl.components_json || "[]")
        : tpl.components_json || [];

    const header = components.find(c => String(c.type).toUpperCase() === "HEADER");

    return String(header?.format || "NONE").toUpperCase();
  } catch {
    return "NONE";
  }
}

function getTemplateHeaderExampleMedia(tpl) {
  if (!tpl) return "";

  try {
    const components =
      typeof tpl.components_json === "string"
        ? JSON.parse(tpl.components_json || "[]")
        : tpl.components_json || [];

    const header = components.find(c => String(c.type).toUpperCase() === "HEADER");

    return header?.example?.header_handle?.[0] || "";
  } catch {
    return "";
  }
}

function formatWaError(errorMessage) {
  if (!errorMessage) return "-";

  try {
    const e = JSON.parse(errorMessage);
    return e.label || e.title || e.details || errorMessage;
  } catch {
    return errorMessage;
  }
}

async function apiFetch(url, options = {}) {
  const token = auth.token;

  if (!token) {
    throw new Error("NO_TOKEN_IN_LOCALSTORAGE");
  }

  const res = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(data.message || "API error");
  }

  return data;
}

async function loadTemplates() {
  try {
    const query = new URLSearchParams({
      search: templateSearch.value || "",
      status: templateStatus.value || "",
    });

    const data = await apiFetch(`/api/wa/templates?${query.toString()}`);
    templates.value = data.data || [];
  } catch (err) {
    showToast(err.message, "error");
  }
}


async function exportRecipients(id, campaignName = "campaign") {
  try {
    const data = await apiFetch(`/api/wa/campaigns/${id}/recipients`);

    const rows = data.data || [];

    if (!rows.length) {
      showToast("No recipients found to export", "error");
      return;
    }

    const exportRows = rows.map(r => ({
      Phone: r.phone || "",
      Name: r.name || "",
      Status: r.status || "",
      Delivery: r.delivery_status || "",
      "Delivered At": r.delivered_at || "",
      "Read At": r.read_at || "",
      "Error Message": formatWaError(r.error_message),
      "Sent At": r.sent_at || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Recipients");

    const safeName = String(campaignName || "campaign")
      .replace(/[^a-z0-9]/gi, "_")
      .toLowerCase();

    XLSX.writeFile(workbook, `${safeName}_recipients.xlsx`);

    showToast("Recipients exported");
  } catch (err) {
    showToast(err.message, "error");
  }
}

async function syncTemplates() {
  try {
    syncing.value = true;

    const data = await apiFetch("/api/wa/templates/sync", {
      method: "POST",
      body: JSON.stringify({}),
    });

    showToast(`Synced ${data.synced || 0} templates`);
    await loadTemplates();
  } catch (err) {
    showToast(err.message, "error");
  } finally {
    syncing.value = false;
  }
}

async function importRecipientsFile(event) {
  try {
    const file = event.target.files?.[0];

    if (!file) return;

    const buffer = await file.arrayBuffer();

    const workbook = XLSX.read(buffer, {
      type: "array",
    });

    const sheet = workbook.Sheets[workbook.SheetNames[0]];

    const rows = XLSX.utils.sheet_to_json(sheet, {
      defval: "",
    });

    if (!rows.length) {
      showToast("File is empty", "error");
      return;
    }

    const imported = rows
      .map(row => {
        const phone =
          row.phone ||
          row.Phone ||
          row.mobile ||
          row.Mobile ||
          row.number ||
          row.Number ||
          "";

        const name =
          row.name ||
          row.Name ||
          row.customer_name ||
          row.CustomerName ||
          "";

        const params = [];

        Object.keys(row).forEach(key => {
          if (key.toLowerCase().startsWith("param")) {
            params.push(String(row[key] || "").trim());
          }
        });

        return {
          phone: String(phone).replace(/\D/g, ""),
          name: String(name || ""),
          bodyParamsText: params.join(", "),
          headerMediaUrl:
            row.headerMediaUrl ||
            row.header_media_url ||
            row.media ||
            "",
          documentFilename:
            row.documentFilename ||
            row.document_filename ||
            "",
        };
      })
      .filter(r => r.phone);

    if (!imported.length) {
      showToast("No valid phone numbers found", "error");
      return;
    }

    recipients.value = imported;

    showToast(`${imported.length} recipients imported`);
  } catch (err) {
    showToast(err.message, "error");
  } finally {
    event.target.value = "";
  }
}

function addRecipient() {
  recipients.value.push({
    phone: "",
    name: "",
    bodyParamsText: "",
    headerMediaUrl: "",
    documentFilename: "",
  });
}

function removeRecipient(index) {
  if (recipients.value.length === 1) {
    recipients.value[0] = {
      phone: "",
      name: "",
      bodyParamsText: "",
      headerMediaUrl: "",
      documentFilename: "",
    };
    return;
  }

  recipients.value.splice(index, 1);
}

function normalizeRecipients() {
  const usableMediaUrl = getUsableHeaderMediaUrl();

  return recipients.value
    .filter(r => String(r.phone || "").trim())
    .map(r => ({
      phone: String(r.phone || "").replace(/\D/g, ""),
      name: r.name || "",
      bodyParams: r.bodyParamsText
        ? r.bodyParamsText.split(",").map(v => v.trim())
        : [],
      headerMediaUrl: r.headerMediaUrl || usableMediaUrl || "",
      documentFilename:
        r.documentFilename ||
        globalDocumentFilename.value ||
        selectedTemplate.value?.default_document_filename ||
        "file.pdf",
    }));
}

async function createCampaign() {
  try {
    if (!campaignName.value.trim()) {
      showToast("Campaign name required", "error");
      return;
    }

    if (!selectedTemplate.value) {
      showToast("Please select template", "error");
      return;
    }

    const finalRecipients = normalizeRecipients();

    if (!finalRecipients.length) {
      showToast("Add at least one recipient", "error");
      return;
    }

    if (isMediaHeader.value) {
      const missingMedia = finalRecipients.find(r => !r.headerMediaUrl);

      if (missingMedia) {
        showToast(
          `Please upload ${getTemplateHeaderType(
            selectedTemplate.value
          )} header media. No saved template media found.`,
          "error"
        );
        return;
      }
    }

    if (bodyParamCount.value > 0) {
      const invalid = finalRecipients.find(
        r => r.bodyParams.length !== bodyParamCount.value
      );

      if (invalid) {
        showToast(
          `Template requires ${bodyParamCount.value} body params for every recipient`,
          "error"
        );
        return;
      }
    }

    if (isScheduled.value && !scheduledAt.value) {
      showToast("Please select schedule date and time", "error");
      return;
    }

    creating.value = true;

    await apiFetch("/api/wa/campaigns", {
      method: "POST",
      body: JSON.stringify({
        campaignName: campaignName.value,
        templateId: selectedTemplate.value.id,
        recipients: finalRecipients,
        batchSize: batchSize.value,
        minDelayMs: minDelayMs.value,
        maxDelayMs: maxDelayMs.value,
        dailyLimit: dailyLimit.value,
        scheduledAt: isScheduled.value ? scheduledAt.value : null,
      }),
    });

    showToast("Campaign created");

    campaignName.value = "";
    isScheduled.value = false;
    scheduledAt.value = "";
    globalHeaderMediaUrl.value = "";
    globalDocumentFilename.value = "";

    recipients.value = [
      {
        phone: "",
        name: "",
        bodyParamsText: "",
        headerMediaUrl: "",
        documentFilename: "",
      },
    ];

    await loadCampaigns();
  } catch (err) {
    showToast(err.message, "error");
  } finally {
    creating.value = false;
  }
}

async function loadCampaigns() {
  try {
    const query = new URLSearchParams({
      page: campaignPage.value,
      limit: campaignLimit.value,
    });

    const data = await apiFetch(`/api/wa/campaigns?${query.toString()}`);

    campaigns.value = data.data || [];
    campaignTotal.value = data.total || 0;
  } catch (err) {
    showToast(err.message, "error");
  }
}

async function startCampaign(id) {
  try {
    await apiFetch(`/api/wa/campaigns/${id}/start`, {
      method: "POST",
      body: JSON.stringify({}),
    });

    showToast("Campaign started");
    await loadCampaigns();
  } catch (err) {
    showToast(err.message, "error");
  }
}

async function pauseCampaign(id) {
  try {
    await apiFetch(`/api/wa/campaigns/${id}/pause`, {
      method: "POST",
      body: JSON.stringify({}),
    });

    showToast("Campaign paused");
    await loadCampaigns();
  } catch (err) {
    showToast(err.message, "error");
  }
}

async function viewRecipients(id) {
  try {
    const data = await apiFetch(`/api/wa/campaigns/${id}/recipients`);

    campaignRecipients.value = data.data || [];
    showRecipients.value = true;
  } catch (err) {
    showToast(err.message, "error");
  }
}

async function uploadHeaderMedia(event) {
  try {
    const file = event.target.files?.[0];
    if (!file) return;

    const fd = new FormData();
    fd.append("file", file);

    const r = await fetch(`${API_BASE}/api/wa/upload-media`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${auth.token}`,
      },
      body: fd,
    });

    const data = await r.json();

    if (!r.ok || data.ok === false) {
      throw new Error(data.message || "Upload failed");
    }

    globalHeaderMediaUrl.value = data.url;
    globalDocumentFilename.value = data.filename || file.name;

    showToast("Header media uploaded");
  } catch (err) {
    showToast(err.message, "error");
  } finally {
    event.target.value = "";
  }
}

async function nextCampaignPage() {
  if (campaignPage.value >= campaignTotalPages.value) return;
  campaignPage.value += 1;
  await loadCampaigns();
}

async function prevCampaignPage() {
  if (campaignPage.value <= 1) return;
  campaignPage.value -= 1;
  await loadCampaigns();
}

async function changeCampaignLimit() {
  campaignPage.value = 1;
  await loadCampaigns();
}

onMounted(async () => {
  await loadTemplates();
  await loadCampaigns();

  refreshTimer = setInterval(() => {
    loadCampaigns();
  }, 5000);
});

onBeforeUnmount(() => {
  if (refreshTimer) clearInterval(refreshTimer);
});
</script>

<style scoped>
/* Keep your existing CSS and add this */

.fallback-media {
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  padding: 10px;
  border-radius: 12px;
}

.warning-media {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  padding: 10px;
  border-radius: 12px;
}

.warning-media small {
  display: block;
  margin-top: 6px;
  color: #9a3412;
}


.page-body {
  padding: 28px;
  min-height: 100%;
  background:
    radial-gradient(circle at top left, rgba(37, 211, 102, 0.12), transparent 30%),
    linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
}

.page-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-head h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: #0f172a;
}

.page-head p {
  margin: 6px 0 0;
  color: #64748b;
}

.wa-grid {
  align-items: stretch;
  display: grid;
  grid-template-columns: 400px minmax(0, 1fr);
  gap: 22px;
}

.panel {
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 22px;
  padding: 20px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
}


/* Make left panel full height */

.wa-grid {

  align-items: stretch;

}

/* Template panel should use full height */

.panel:first-child {

  display: flex;

  flex-direction: column;

  height: calc(100vh - 140px); /* adjust based on header */

}

/* Scrollable area */

.template-list {

  flex: 1;

  overflow-y: auto;

  padding-right: 6px;

}

/* Smooth modern scrollbar */

.template-list::-webkit-scrollbar {

  width: 6px;

}

.template-list::-webkit-scrollbar-thumb {

  background: #cbd5e1;

  border-radius: 10px;

}

.template-list::-webkit-scrollbar-thumb:hover {

  background: #94a3b8;

}

.panel:hover {
  transform: translateY(-2px);
}

.panel-head h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.toolbar {
  display: grid;
  grid-template-columns: 1fr 130px auto;
  gap: 10px;
  margin: 16px 0;
}

input,
select {
  width: 100%;
  height: 42px;
  border: 1px solid #dbe3ee;
  border-radius: 13px;
  padding: 0 13px;
  background: #ffffff;
  color: #0f172a;
  outline: none;
  transition: 0.18s ease;
}

input:focus,
select:focus {
  border-color: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.12);
}

.template-card {
  position: relative;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 14px;
  margin-bottom: 12px;
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  cursor: pointer;
  transition: 0.2s ease;
}

.template-card:hover,
.template-card.active {
  border-color: #25d366;
  box-shadow: 0 12px 28px rgba(37, 211, 102, 0.16);
  transform: translateY(-1px);
}

.template-card.active::before {
  content: "";
  position: absolute;
  left: -1px;
  top: 16px;
  bottom: 16px;
  width: 4px;
  border-radius: 999px;
  background: #25d366;
}

.template-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.template-meta {
  margin-top: 6px;
  font-size: 12px;
  color: #64748b;
}

.template-card p {
  margin: 10px 0 0;
  color: #334155;
  font-size: 13px;
  line-height: 1.55;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
}

.status-badge.active {
  background: #dcfce7;
  color: #166534;
}

.status-badge.draft {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.failed {
  background: #fee2e2;
  color: #991b1b;
}
.small-btn {
  background: linear-gradient(135deg, #25d366, #16a34a);
  color: #fff;
  box-shadow: 0 10px 22px rgba(22, 163, 74, 0.22);
  padding: 8px;
  border: none;
  margin: 1rem;
  border-radius: 10px;
  cursor: pointer;

}

.small-btn:hover {

  transform: translateY(-1px);

  opacity: 0.9;

}

.small-btn:active {

  transform: scale(0.97);

}

.preview-box,
.import-box,
.media-box,
.safe-settings {
  border-radius: 18px;
  padding: 15px;
  margin: 14px 0;
}

.preview-box {
  border: 1px solid #e0e7ff;
  background: linear-gradient(180deg, #f8fafc, #eef2ff);
}

.media-box {
  border: 1px dashed #22c55e;
  background: linear-gradient(180deg, #f0fdf4, #ecfdf5);
}

.import-box {
  border: 1px dashed #94a3b8;
  background: #f8fafc;
}

.safe-settings {
  border: 1px solid #bfdbfe;
  background: linear-gradient(180deg, #eff6ff, #f8fbff);
}

.safe-settings h4,
.steps-header h3 {
  margin: 0 0 12px;
  color: #0f172a;
  font-weight: 800;
}

.safe-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.safe-grid label,
.import-box label,
.media-box label,
.form-group label {
  font-size: 12px;
  font-weight: 800;
  color: #334155;
  margin-bottom: 6px;
  display: block;
}

.safe-grid small,
.import-box small,
.media-box small {
  display: block;
  margin-top: 6px;
  color: #64748b;
  font-size: 11px;
  line-height: 1.45;
}

.recipient-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1.4fr auto;
  gap: 10px;
  margin-bottom: 10px;
}

.primary-btn,
.manage-btn,
.secondary-btn,
.test-btn,
.warning-btn,
.edit-btn,
.remove-btn {
  height: 40px;
  border: none;
  border-radius: 13px;
  padding: 0 15px;
  font-weight: 800;
  cursor: pointer;
  transition: 0.18s ease;
  white-space: nowrap;
}

.primary-btn {
  background: linear-gradient(135deg, #25d366, #16a34a);
  color: #fff;
  box-shadow: 0 10px 22px rgba(22, 163, 74, 0.22);
}

.primary-btn:hover,
.manage-btn:hover,
.secondary-btn:hover,
.test-btn:hover,
.warning-btn:hover,
.edit-btn:hover {
  transform: translateY(-1px);
}

.manage-btn,
.secondary-btn,
.edit-btn {
  background: #ffffff;
  color: #173a67;
  border: 1px solid #dbe3ee;
}

.test-btn {
  background: #22c55e;
  color: white;
}

.warning-btn {
  background: #f59e0b;
  color: white;
}

.remove-btn {
  width: 40px;
  background: #fee2e2;
  color: #b91c1c;
}

.full-btn {
  width: 100%;
  margin-top: 12px;
}

.campaign-panel {
  margin-top: 22px;
}

.table-wrap {
  overflow-x: auto;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

th {
  padding: 13px;
  text-align: left;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569;
  background: #f8fafc;
}

td {
  padding: 13px;
  font-size: 13px;
  color: #334155;
  border-top: 1px solid #eef2f7;
}

tr:hover td {
  background: #f8fafc;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: #ffffff;
  border-top: 1px solid #eef2f7;
}

.page-info {
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}

.page-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(6px);
  display: grid;
  place-items: center;
  z-index: 999;
}

.modal {
  width: min(1100px, 94vw);
  max-height: 86vh;
  overflow: auto;
  background: #ffffff;
  border-radius: 24px;
  padding: 18px;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.28);
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.close-btn {
  border: none;
  background: #fee2e2;
  color: #991b1b;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  font-size: 20px;
  cursor: pointer;
}

.toast {
  position: fixed;
  right: 22px;
  bottom: 22px;
  padding: 14px 18px;
  border-radius: 15px;
  background: #16a34a;
  color: white;
  font-weight: 800;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.2);
  z-index: 1200;
}

.toast.error {
  background: #ef4444;
}


.schedule-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: center;
}

.checkbox-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 10px 12px;
  border-radius: 10px;
}

.checkbox-row span {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.switch input:checked + .slider {
  box-shadow: 0 0 10px rgba(37, 211, 102, 0.6);
}

.toggle-row span {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

/* SWITCH */
.switch {
  position: relative;
  width: 46px;
  height: 26px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* slider */
.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #cbd5e1;
  border-radius: 999px;
  transition: 0.3s;
}

.datetime-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ffffff;
  border: 1px solid #dbe3ee;
  border-radius: 14px;
  transition: 0.2s;
}

.datetime-wrapper:hover {
  border-color: #25d366;
  box-shadow: 0 0 0 4px rgba(37, 211, 102, 0.12);
}

.datetime-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  color: #0f172a;
}

/* Remove ugly default icon */
.datetime-input::-webkit-calendar-picker-indicator {
  filter: invert(0.4);
  cursor: pointer;
}


.campaign-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.date-icon {
  font-size: 16px;
  opacity: 0.75;
  padding-left: 12px;
}

.datetime-wrapper {
  padding: 0;
  cursor: pointer;
}

.datetime-input {
  cursor: pointer;
}



.campaign-builder-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 18px;
  align-items: start;
}

.campaign-inputs {
  min-width: 0;
}

.campaign-preview {
  min-width: 0;
}


.export-btn {
  height: 40px;
  border: none;
  border-radius: 13px;
  padding: 0 15px;
  font-weight: 800;
  cursor: pointer;
  transition: 0.18s ease;
  white-space: nowrap;
  background: #0f172a;
  color: #ffffff;
}

.export-btn:hover {
  transform: translateY(-1px);
}

.preview-sticky {
  position: sticky;
  top: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.preview-empty {
  min-height: 320px;
  border: 1px dashed #cbd5e1;
  border-radius: 18px;
  display: grid;
  place-items: center;
  color: #64748b;
  font-weight: 700;
  background: #f8fafc;
  text-align: center;
  padding: 20px;
}

@media (max-width: 1200px) {
  .campaign-builder-grid {
    grid-template-columns: 1fr;
  }

  .preview-sticky {
    position: static;
  }
}

@media (max-width: 700px) {
  .campaign-form-grid {
    grid-template-columns: 1fr;
  }
}


/* Icon */
.icon {
  font-size: 16px;
  opacity: 0.7;
}

/* Live preview */
.hint {
  font-size: 12px;
  color: #16a34a;
  font-weight: 600;
}

/* circle */
.slider::before {
  content: "";
  position: absolute;
  height: 20px;
  width: 20px;
  left: 3px;
  top: 3px;
  background: white;
  border-radius: 50%;
  transition: 0.3s;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
}

/* checked */
.switch input:checked + .slider {
  background: linear-gradient(135deg, #25d366, #16a34a);
}

.switch input:checked + .slider::before {
  transform: translateX(20px);
}


.datetime-wrapper {
  cursor: pointer;
}

@media (max-width: 1100px) {
  .wa-grid {
    grid-template-columns: 1fr;
  }

  .safe-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 700px) {
  .page-head,
  .pagination {
    flex-direction: column;
    align-items: flex-start;
  }

  .toolbar,
  .recipient-row,
  .safe-grid {
    grid-template-columns: 1fr;
  }

  .page-actions {
    flex-wrap: wrap;
  }
}

</style>